import { DownloadOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Spin,
  Row,
  Col,
  Input,
  Table,
  Popover,
  Tag,
  Tooltip,
  Button,
} from "antd";
import { ColumnsType } from "antd/es/table";
import React from "react";
import { userDetailsProps } from "../../Services/UserService";
import { adminContext } from "../../Common/PageRoute";
import { IPagingPagingItems } from "../../Common/CommonComponent";
import AMLReportService from "../../Services/AMLReportService";
import dayjs, { Dayjs } from "dayjs";
import { IIdName } from "../../Services/CommonService";
import CustomRangePicker from "../../Common/Custom Components/Ant design/RangePicker";

const { Search } = Input;

interface ListDataType {
  key: React.Key;
  id: number;
  companyName: string;
  allMembers: IIdName[];
}

interface ListData {
  items: ListDataType[];
  totalItems: number;
}

const AMLReport = ({ practiceId, practiceName }: { practiceId: number; practiceName: string }) => {
  const { openNotification } = React.useContext(adminContext);
  const [loading, setLoading] = React.useState(false);
  const [totalItems, setTotalItems] = React.useState(0);
  const [totalCompletedCount, setTotalCompletedCount] = React.useState(0);

  const [pagingItems, setPagingItems] = React.useState<IPagingPagingItems>({
    totalRecords: 0,
    currentPage: 1,
    pageSize: 15,
  });
  const [listParams, setListParams] = React.useState<{
    refresh: boolean;
    search: string;
    fromDate: Dayjs | null;
    toDate: Dayjs | null;
    sortDir: string;
    sortCol: string;
  }>({
    refresh: true,
    search: "",
    fromDate: dayjs().startOf("month"),
    toDate: dayjs().endOf("day"),
    sortDir: "ascend",
    sortCol: "CompanyName",
  });

  const setListParamsAndRefresh = (key: any, value: any) => {
    setPagingItems({ ...pagingItems, currentPage: 1, pageSize: 15 });
    setListParams((p: any) => ({ ...p, [key]: value }));
  };

  const handleDownload = async () => {
    if (!practiceId) return;
    try {
      const res = await AMLReportService.getAMLStatsDownload(
        practiceId,
        practiceName,
        listParams.search,
        listParams.fromDate?.startOf("day").toISOString(),
        listParams.toDate?.endOf("day").toISOString(),
        listParams.sortCol ?? null,
        listParams.sortDir ?? null,
      );

      if (res.status === "done") {
        console.log("Download successful");
        openNotification("success", "Report downloaded successfully.");
      } else {
        console.error("Download failed", res.error);
        openNotification("error", "Download failed. Please try again.");
      }
    } catch (err) {
      console.error("Download failed", err);
      openNotification("error", "Download failed. Please try again.");
    }
  };

  return (
    <>
      <Spin
        spinning={loading}
        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
      >
        <Row
          style={{
            backgroundColor: "white",
          }}
        >
          <Col span={24}>
            <Row gutter={[8, 8]} className="py-2" align="middle" justify="end">
              <Col><Tag color="green">Total Completed  - {totalCompletedCount}</Tag></Col>
              <Col>
                <Tooltip
                  title="Download AML report"
                  color="white"
                  overlayInnerStyle={{ color: "rgba(0,0,0,0.85)" }}
                >
                  <Button
                    type="default"
                    onClick={handleDownload}
                    disabled={!totalItems}
                    icon={<DownloadOutlined />}
                  />
                </Tooltip>
              </Col>
              <Col xs={24} sm={12} md={5} lg={5} xl={4} xxl={3}>
                <Search
                  placeholder="Search"
                  onSearch={(val: string) =>
                    setListParamsAndRefresh("search", val)
                  }
                  onChange={(e) =>
                    e.target.value === ""
                      ? setListParamsAndRefresh("search", "")
                      : null
                  }
                  style={{ width: "100%" }}
                />
              </Col>
              {/* Date Range Picker */}
              <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
                <CustomRangePicker
                  value={[listParams.fromDate, listParams.toDate]}
                  allowClear={false}
                  onChange={(dates) => {
                    setListParams((prev) => ({
                      ...prev,
                      fromDate: dates?.[0] || null,
                      toDate: dates?.[1] || null,
                    }));
                    setPagingItems({ ...pagingItems, currentPage: 1 });
                  }}
                />
              </Col>
            </Row>
          </Col>

          <Col span={24}>
            <AMLReportTable
              practiceId={practiceId}
              setLoading={setLoading}
              listParams={listParams}
              setListParams={(v: any) => {
                setListParams(v);
              }}
              pagingItems={pagingItems}
              setPagingItems={(v: IPagingPagingItems) => {
                setPagingItems(v);
              }}
              setTotalItems={setTotalItems}
              setTotalCompletedCount={setTotalCompletedCount}
            />
          </Col>
        </Row>
      </Spin>
    </>
  );
};

export default AMLReport;

export const AMLReportTable = ({
  practiceId,
  setLoading,
  listParams,
  setListParams,
  pagingItems,
  setPagingItems,
  setTotalItems,
  setTotalCompletedCount
}: userDetailsProps & { setTotalItems?: (total: number) => void } & { setTotalCompletedCount?: (total: number) => void }) => {
  const { openNotification } = React.useContext(adminContext);
  const [usersList, setUsersList] = React.useState<ListData>({
    items: [],
    totalItems: 0,
  });
  const onTablePageChange = (
    paging: { current?: number; pageSize?: number },
    filter: Record<string, any>,
    sort: { order?: string; columnKey?: string },
  ) => {
    setListParams &&
      setListParams((prev: any) => {
        return {
          ...prev,
          sortDir: sort?.order,
          sortCol: sort?.columnKey,
        };
      });
    if (paging?.current && paging?.pageSize) {
      setPagingItems((prev: any) => {
        return {
          ...prev,
          currentPage: paging?.current,
          pageSize: paging?.pageSize,
        };
      });
    }
  };

  const columns: ColumnsType<ListDataType> = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      width: "5%",
      render: (_: any, record: any, index: any) => (
        <p>
          {pagingItems.currentPage > 1
            ? pagingItems.pageSize * (pagingItems.currentPage - 1) + index + 1
            : index + 1}
        </p>
      ),
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "CompanyName",
      sorter: true,
      defaultSortOrder: "ascend",
      width: "25%",
    },
    {
      title: "Members",
      dataIndex: "allMembers",
      key: "allMembers",
      width: "25%",
      render: (_: any, record: ListDataType) => {
        const members = record?.allMembers || [];

        const name = members[0]?.name;
        const remainingCount = members.length - 1;
        return (
          <>
            {members.length > 0 && (
              <Row gutter={8}>
                <Col>
                  <span>{name}</span>
                </Col>
                <Col>
                  <Popover
                    placement="bottom"
                    content={
                      <Table
                        size="small"
                        pagination={false}
                        dataSource={members.map((x, i) => ({
                          key: i,
                          sno: i + 1,
                          name: x.name,
                        }))}
                        columns={[
                          {
                            title: "S.No.",
                            dataIndex: "sno",
                            width: 60,
                          },
                          {
                            title: "Member name",
                            dataIndex: "name",
                          },
                        ]}
                      />
                    }
                  >
                    <div
                      style={{
                        maxWidth: 200,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      {remainingCount > 0 && (
                        <Tag color="blue">+{remainingCount}</Tag>
                      )}
                    </div>
                  </Popover>
                </Col>
              </Row>
            )}
          </>
        );
      },
    },
    {
      title: "Total",
      dataIndex: "totalDone",
      width: "20%",
      render: (_: any, record: any) => (
        <div>
          {record.allMembers?.length}
        </div>
      ),
    }

  ];

  const getItems = async (start: any, length: any) => {
    let res = await AMLReportService.getAMLReport(
      practiceId,
      start,
      length,
      listParams?.search ?? null,
      listParams.fromDate?.startOf("day").toISOString(),
      listParams.toDate?.endOf("day").toISOString(),
      listParams?.sortCol ?? null,
      listParams?.sortDir ?? null,
    );
    if (res?.status) {
      if (setLoading) setLoading(false);
      var data: ListDataType[] = res?.result?.items.map((item: any) => {
        var itemWithKey = {
          key: item?.id,
          id: item?.id,
          allMembers: item?.allMembers,
          companyName: item?.companyName,
          sno: item?.sno,
          status: item?.status,
        };
        return itemWithKey;
      });
      setUsersList({
        totalItems: res?.result?.totalRecords,
        items: data,
      });
      // Update parent component's totalItems state
      if (setTotalItems && setTotalCompletedCount) {
        setTotalItems(res?.result?.totalRecords);
        setTotalCompletedCount(res?.result?.totalCompletedCount)
      }
    } else {
      openNotification("error", res.message);
      if (setLoading) setLoading(false);
    }
  };

  React.useEffect(() => {
    if (setLoading) setLoading(true);
    getItems(
      (pagingItems.currentPage - 1) * pagingItems.pageSize,
      pagingItems.pageSize,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParams, practiceId, pagingItems]);

  return (
    <>
      <Row>
        <Col span={24}>
          <Table
            dataSource={usersList?.items}
            columns={columns}
            size="small"
            className="border-top-table"
            locale={{
              triggerDesc: "",
              triggerAsc: "",
              cancelSort: "",
            }}
            summary={() => {
              const totalPageCompletedCount = usersList?.items?.reduce(
                (sum: number, item: any) => sum + (item?.allMembers?.length || 0),
                0
              );
              // align="right"
              return (
                <Table.Summary fixed>
                  <Table.Summary.Row
                    style={{
                      backgroundColor: "#f5f8ff",
                      fontWeight: "bold",
                      color: "#4d4d4d",
                    }}
                  >
                    <Table.Summary.Cell
                      index={1}
                      className="fw600"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell index={2} className="fw600">
                      Page Total
                    </Table.Summary.Cell>
                    <Table.Summary.Cell
                      index={3}
                      className="fw600"
                    ></Table.Summary.Cell>
                    <Table.Summary.Cell index={4}>
                      {totalPageCompletedCount}
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              );
            }}
            scroll={
              (usersList?.items?.length || 0) > 15
                ? { y: "calc(100vh - 340px)", x: "max-content" }
                : undefined
            }
            pagination={{
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} items`,
              current: pagingItems.currentPage,
              pageSize: pagingItems.pageSize,
              showSizeChanger: true,
              total: usersList?.totalItems,
              pageSizeOptions: ["15", "25", "50", "100"],
            }}
            onChange={(paging, filter, sort: any) =>
              onTablePageChange(paging, filter, sort)
            }
          />
        </Col>
      </Row>
    </>
  );
};
